import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toLocalISODate, monthLabel } from "../../lib/dates";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin, Video, Download, Bell, CheckCheck, Phone, Mail,
  CreditCard, Loader2, Vote as VoteIcon, Share2, Lightbulb, X, CheckCircle2, Sparkles } from "lucide-react";
import { AddToCalendar } from "../../components/AddToCalendar";
import api, { formatError } from "../../lib/api";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import GoodStandingCard, { StatusPill } from "../../components/GoodStandingCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useLang } from "../../i18n/LanguageContext";
import { useLive } from "../../components/LiveFeed";
import { FileUpload, AuthImage, downloadPdf } from "../../components/FileUpload";
import { EventFlyer } from "../../components/EventFlyer";
import { safeHref } from "../../lib/safeHref";
import { AccountSelect } from "../../components/AccountSelect";
import { useTips } from "../../components/Tips";
import { RsvpBadge } from "../../components/RsvpBadge";
import { PushToggle } from "../../components/PushToggle";
import { SecurityPanel } from "../../components/SecurityPanel";
import { CollapsibleList } from "../../components/CollapsibleList";
import { SecurityNotices } from "../../components/SecurityExtras";
import { MemberCardDialog } from "../../components/MemberCardDialog";
import { EventsBrowser } from "../../components/EventsBrowser";
import { NotificationGroups } from "../../components/NotificationGroups";
import { MeetingsAttended } from "../../components/MeetingsAttended";
import { StreakBadge } from "../../components/StreakBadge";
import { PhotoNudgeBanner } from "../../components/PhotoNudgeBanner";
import { TeamupCalendar } from "../../components/TeamupCalendar";
import { MinutesList } from "../../components/MinutesList";
import { PastEvents } from "../../components/PastEvents";
import { BirthdayStrip } from "../../components/BirthdayStrip";
import { MyConsentCard } from "../../components/MediaConsent";
import { timeRange } from "../../lib/dates";

const CLAIM_STANDING = { confirmed: "GOOD STANDING", pending: "DUES PENDING" };
const claimStanding = (status) => CLAIM_STANDING[status] || "NOT IN GOOD STANDING";
import { ElectionResults } from "../admin/Insights";

function Header({ title, sub }) {
  const { t } = useLang();
  return (
    <div className="mb-8">
      <p className="eyebrow text-gold">{t("Member Portal")}</p>
      <h1 className="mt-1 font-serif-display text-4xl font-bold text-crimson">{t(title)}</h1>
      {sub && <p className="mt-1 text-muted-foreground">{t(sub)}</p>}
    </div>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const { lastEvent } = useLive();
  const { data: standing } = useApi("/members/standing");
  const { data: events } = useApi("/events?upcoming=true");
  const { data: notif, reload: reloadNotif } = useApi("/notifications");
  useEffect(() => { if (lastEvent) reloadNotif(); }, [lastEvent, reloadNotif]);
  const nextEvent = (events || [])[0];
  return (
    <div data-testid="dashboard-page">
      <Header title={`${t("Akwaaba")}, ${user?.name?.split(" ")[0]}!`} sub="Here is your association at a glance." />
      <PhotoNudgeBanner />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2"><GoodStandingCard standing={standing} member={user} /></div>
        <div className="space-y-4">
          <div className="glass rounded-2xl p-5">
            <p className="eyebrow text-gold">{t("Balance Due")}</p>
            <p className="mt-1 font-mono-accent text-3xl font-bold text-crimson">
              ${standing ? standing.balance.balance.toFixed(2) : "—"}
            </p>
            <Button asChild size="sm" className="mt-3 w-full bg-crimson hover:bg-crimson-hover" data-testid="dash-pay-btn">
              <Link to="/portal/dues">{t("Pay now")}</Link>
            </Button>
          </div>
          <div className="glass rounded-2xl p-5">
            <p className="eyebrow text-gold">{t("Attendance")} ({new Date().getFullYear()})</p>
            <p className="mt-1 font-mono-accent text-3xl font-bold text-palm">
              {standing ? `${standing.attendance.percentage}%` : "—"}
            </p>
            <p className="text-xs text-muted-foreground">{t("50% required")} · {standing?.attendance.attended}/{standing?.attendance.total_meetings} {t("meetings")}</p>
            <StreakBadge streak={standing?.attendance.streak} />
          </div>
        </div>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Next meeting")}</h3>
          {nextEvent ? (
            <div className="mt-3">
              <p className="font-semibold">{nextEvent.title}</p>
              <p className="mt-1 flex items-center gap-2 text-sm text-palm"><Calendar size={15} /> {nextEvent.date}</p>
              <p className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={15} /> {nextEvent.location}</p>
              <Button asChild size="sm" variant="outline" className="mt-3"><Link to="/portal/events">{t("View events")}</Link></Button>
            </div>
          ) : <p className="mt-3 text-muted-foreground">{t("No upcoming events.")}</p>}
        </div>
        <div className="glass rounded-2xl p-6" data-testid="recent-notices">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Recent notices")}</h3>
            {notif?.unread > 0 && <span className="rounded-full bg-crimson px-2 py-0.5 text-xs text-parchment">{notif.unread} {t("new")}</span>}
          </div>
          <ul className="mt-3 space-y-2">
            {(notif?.notifications || []).slice(0, 4).map((n) => (
              <li key={n.id} className="rounded-lg bg-parchment-card p-3 text-sm border border-gold/20">
                <p className="font-medium">{n.title}</p><p className="text-muted-foreground">{n.body}</p>
              </li>
            ))}
            {(!notif || notif.notifications.length === 0) && <p className="text-muted-foreground">{t("No notices yet.")}</p>}
          </ul>
          <div className="mt-4"><PushToggle compact /></div>
        </div>
      </div>
      <BirthdayStrip />
    </div>
  );
}

export function Standing() {
  const { user } = useAuth();
  const { t } = useLang();
  const { data: standing } = useApi("/members/standing");
  const shareCard = async () => {
    try {
      const r = await api.get("/members/me/card.png", { responseType: "blob" });
      const file = new File([r.data], "okuapemman-membership-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Okuapemman membership card" });
      } else {
        const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
        toast.success("Card image saved");
      }
    } catch { toast.error("Could not create card image"); }
  };
  return (
    <div data-testid="standing-page">
      <Header title="Good Standing" sub="Your live status, computed from dues, levies, and attendance." />
      <div className="grid gap-6 lg:grid-cols-2">
        <GoodStandingCard standing={standing} member={user} />
        {standing && (
          <div className="glass rounded-2xl p-6">
            <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Breakdown")}</h3>
            <dl className="mt-4 space-y-3 text-sm">
              {[[standing.balance.grandfathered ? "Registration fee (waived — grandfathered)" : "Registration fee", standing.balance.registration_fee],
                [`Monthly dues × ${standing.balance.months_due} (since ${(standing.balance.dues_start || "").slice(0, 7)})`, standing.balance.dues_total],
                ["Levies", standing.balance.levy_total],
                ["Total obligation", standing.balance.obligation],
                ["Paid toward balance", standing.balance.paid_total]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-gold/15 pb-2">
                  <dt className="text-muted-foreground">{t(k)}</dt><dd className="font-mono-accent">${Number(v).toFixed(2)}</dd>
                </div>
              ))}
              <div className="flex justify-between pt-1">
                <dt className="font-bold">{t("Balance")}</dt>
                <dd className="font-mono-accent text-lg font-bold text-crimson">${standing.balance.balance.toFixed(2)}</dd>
              </div>
              {standing.balance.other_total > 0 && (
                <div className="mt-2 rounded-lg bg-gold/10 p-3" data-testid="standing-gifts">
                  <div className="flex justify-between"><dt className="font-semibold text-crimson">{t("Gifts & donations (thank you!)")}</dt><dd className="font-mono-accent font-semibold">${standing.balance.other_total.toFixed(2)}</dd></div>
                  {standing.balance.other_by_account.map((o) => (
                    <div key={o.account} className="flex justify-between text-xs text-muted-foreground"><dt>{o.account}</dt><dd className="font-mono-accent">${o.amount.toFixed(2)}</dd></div>
                  ))}
                  <p className="mt-1 text-[11px] text-muted-foreground">{t("Recorded separately — not applied to your balance due.")}</p>
                </div>
              )}
            </dl>
            <p className="mt-4 text-sm">{t("Welfare eligibility:")} {standing.welfare_eligible
              ? <span className="text-palm font-semibold">{t("Eligible ✓")}</span>
              : <span className="text-destructive">{t("Requires 1yr membership + good standing")}</span>}
              {standing.balance.grandfathered && <span className="ml-2 rounded-full bg-gold/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-crimson" data-testid="standing-grandfathered">{t("Grandfathered member")}</span>}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="gap-1" data-testid="standing-statement-pdf"
                onClick={() => downloadPdf("/members/me/statement.pdf", "arrears-statement.pdf")}>
                <Download size={14} /> {t("Arrears statement (PDF)")}
              </Button>
              <Button size="sm" className="gap-1 bg-palm hover:bg-palm/90" data-testid="share-card-btn" onClick={shareCard}>
                <Share2 size={14} /> {t("Save / share my card")}
              </Button>
            </div>
          </div>
        )}
        <MeetingsAttended />
      </div>
    </div>
  );
}

export function Dues() {
  const { t } = useLang();
  const { data: balance, reload } = useApi("/dues/balance");
  const { data: levies } = useApi("/levies");
  const { data: payInfo } = useApi("/settings/payment");
  const { data: eb } = useApi("/dues/early-bird");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState("");
  const [claim, setClaim] = useState({ amount: "", method: "zelle", reference: "", note: "", receipt_url: "", account_id: "" });
  const setClaimAccount = React.useCallback((v) => setClaim((c) => ({ ...c, account_id: v })), []);

  useEffect(() => { if (balance) setAmount((a) => a || (balance.balance > 0 ? balance.balance.toFixed(2) : Number(balance.annual_dues || 240).toFixed(2))); }, [balance]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: parseFloat(amount), origin_url: window.location.origin, description: "Membership payment", account_id: account,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setBusy(false); }
  };

  const submitClaim = async () => {
    try {
      await api.post("/payments/offline-claim", { ...claim, amount: parseFloat(claim.amount) });
      toast.success("Claim submitted. An officer will confirm it shortly.");
      setClaim((c) => ({ amount: "", method: "zelle", reference: "", note: "", receipt_url: "", account_id: c.account_id }));
      reload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="dues-page">
      <Header title="Dues & Levies" sub="Pay your dues and levies, then submit an offline payment claim for confirmation." />
      {eb?.promo && (eb.earned
        ? <div className="mb-5 flex items-center gap-3 rounded-xl border border-palm/40 bg-palm/10 p-4 text-sm text-palm" data-testid="early-bird-earned"><CheckCircle2 size={18} className="shrink-0" /><p><b>{t("Early-payment discount applied")}</b> — {eb.earned_note}</p></div>
        : <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-gold bg-gold-light/60 p-4" data-testid="early-bird-promo">
          <div className="flex items-start gap-3"><Sparkles size={20} className="mt-0.5 shrink-0 text-crimson" />
            <div><p className="font-serif-display text-base font-bold text-crimson">{t("Pay")} ${Number(eb.min_payment).toFixed(0)} {t("toward dues by")} {eb.deadline_label} {t("and save")} ${Number(eb.discount).toFixed(0)}</p>
              <p className="text-xs text-foreground/80">{t("One single Dues payment of")} ${Number(eb.min_payment).toFixed(0)}+ {t("received by")} {eb.deadline_label} {t("earns an automatic")} ${Number(eb.discount).toFixed(0)} {t("credit — your whole year covered. Members with arrears from previous years don't qualify.")}</p></div></div>
          <Button size="sm" variant="outline" className="border-crimson text-crimson" onClick={() => { setAmount(Number(eb.min_payment).toFixed(2)); setClaim((c) => ({ ...c, amount: Number(eb.min_payment).toFixed(2) })); }} data-testid="early-bird-prefill">{t("Use")} ${Number(eb.min_payment).toFixed(0)}</Button>
        </div>)}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6">
          <p className="eyebrow text-gold">{t("Current balance")}</p>
          <p className="mt-1 font-mono-accent text-4xl font-bold text-crimson">${balance ? balance.balance.toFixed(2) : "—"}</p>
          {balance && (
            balance.months_ahead > 0
              ? <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-palm/15 px-3 py-1 text-xs font-semibold text-palm" data-testid="dues-months-ahead-badge"><CheckCircle2 size={13} /> {t("Paid")} {balance.months_ahead} {balance.months_ahead === 1 ? t("month") : t("months")} {t("ahead")} · {t("covered through")} {monthLabel(balance.paid_through)}</p>
              : <p className="text-xs text-muted-foreground" data-testid="dues-arrears-line">{balance.months_in_arrears} {t("months in arrears")} · {t("dues since")} {(balance.dues_start || "").slice(0, 7)}{balance.balance <= 0 && balance.paid_through ? ` · ${t("covered through")} ${monthLabel(balance.paid_through)}` : ""}</p>
          )}
          <div className="mt-6 space-y-3">
            {payInfo?.online_payments === false ? (
              <div className="rounded-xl border border-dashed border-gold/60 bg-gold-light/40 p-4" data-testid="online-payments-coming-soon">
                <p className="flex items-center gap-2 font-semibold text-crimson"><CreditCard size={16} /> {t("Pay with card")} <span className="rounded-full bg-crimson px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-parchment">{t("Coming soon")}</span></p>
                <p className="mt-1 text-sm text-muted-foreground" data-testid="pay-guidance-text">{payInfo?.guidance?.text || t("Online card payments are being set up. For now, please pay using one of the methods below and submit an offline payment claim so an officer can confirm it.")}</p>
                {payInfo && (
                  <ul className="mt-3 space-y-1 text-sm" data-testid="offline-methods">
                    {payInfo.cashapp_status === "available" && payInfo.cashapp && <li><span className="font-semibold">Cash App:</span> <span className="font-mono-accent select-all">{payInfo.cashapp}</span></li>}
                    {payInfo.cash_at_meeting && <li><span className="font-semibold">{t("Cash")}:</span> {t("at the next general meeting")}</li>}
                    {payInfo.zelle_status === "available" && payInfo.zelle && <li><span className="font-semibold">Zelle:</span> <span className="font-mono-accent select-all">{payInfo.zelle}</span></li>}
                    {payInfo.check_status === "available" && payInfo.check_payable_to && <li><span className="font-semibold">{t("Checks payable to")}:</span> {payInfo.check_payable_to}</li>}
                    {payInfo.guidance?.coming_soon?.length > 0 && <li className="text-xs text-muted-foreground" data-testid="coming-soon-methods">{payInfo.guidance.coming_soon.join(", ")} — {t("coming soon")}</li>}
                  </ul>
                )}
              </div>
            ) : (<>
            <AccountSelect value={account} onChange={setAccount} testId="dues-account" label={t("Pay into account")} />
            <div><Label>{t("Amount to pay (USD)")}</Label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="dues-amount" />
            <Button onClick={pay} disabled={busy || !amount} className="mt-3 w-full bg-crimson hover:bg-crimson-hover" data-testid="dues-pay-submit">
              {busy ? <Loader2 className="animate-spin" /> : <><CreditCard size={16} className="mr-1" /> {t("Pay with card")}</>}
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">Test card: 4242 4242 4242 4242 · any future expiry · any CVC</p>
            </div>
            </>)}
          </div>
          {levies?.length > 0 && (
            <div className="mt-6 border-t border-gold/20 pt-4">
              <p className="eyebrow text-gold">{t("Active levies")}</p>
              {levies.map((l) => (
                <div key={l.id} className="mt-2 flex justify-between text-sm">
                  <span>{l.name}</span><span className="font-mono-accent">${l.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold text-palm">{t("Offline payment claim")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("Paid by Zelle, Cash App, cash, or check? Tell us and an officer will confirm it.")}</p>
          <div className="mt-4 space-y-3">
            <AccountSelect value={claim.account_id} onChange={setClaimAccount} testId="claim-account" label={t("Pay into account")} />
            <div><Label>{t("Amount")}</Label><Input type="number" step="0.01" value={claim.amount} onChange={(e) => setClaim({ ...claim, amount: e.target.value })} data-testid="claim-amount" /></div>
            <div>
              <Label>{t("Method")}</Label>
              <Select value={claim.method} onValueChange={(v) => setClaim({ ...claim, method: v })}>
                <SelectTrigger data-testid="claim-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="cashapp">Cash App</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("Reference / confirmation #")}</Label><Input value={claim.reference} onChange={(e) => setClaim({ ...claim, reference: e.target.value })} data-testid="claim-ref" /></div>
            <div><Label>{t("Note")}</Label><Textarea value={claim.note} onChange={(e) => setClaim({ ...claim, note: e.target.value })} data-testid="claim-note" /></div>
            <div><Label>{t("Receipt photo / screenshot (optional)")}</Label>
              <FileUpload purpose="payment_receipt" testId="claim-receipt-upload" onUploaded={(f) => setClaim((c) => ({ ...c, receipt_url: f.url }))} /></div>
            <Button onClick={submitClaim} disabled={!claim.amount} className="w-full bg-palm hover:bg-palm/90" data-testid="claim-submit">
              {t("I've sent it — submit claim")}
            </Button>
          </div>
          <div className="mt-4 space-y-1 rounded-lg bg-gold-light p-3 text-xs text-crimson" data-testid="payment-info">
            {payInfo?.cashapp_status === "available" && payInfo?.cashapp && <p>Cash App: <strong data-testid="cashapp-tag">{payInfo.cashapp}</strong></p>}
            {payInfo?.cash_at_meeting && <p>{t("Cash")}: <strong>{t("at the next general meeting")}</strong></p>}
            {payInfo?.zelle_status === "available" && payInfo?.zelle && <p>Zelle: <strong>{payInfo.zelle}</strong></p>}
            {payInfo?.check_status === "available" && payInfo?.check_payable_to && <p>Checks payable to: <strong>{payInfo.check_payable_to}</strong>.</p>}
            <p>{t("Include your name in the memo.")}{payInfo?.guidance?.coming_soon?.length > 0 && <> {payInfo.guidance.coming_soon.join(", ")} — {t("coming soon")}.</>}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Payments() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { data: history, reload } = useApi("/payments/history");
  const { data: balance } = useApi("/dues/balance");
  const { data: claims } = useApi("/payments/offline-claim");
  const [polling, setPolling] = useState(!!sessionId);
  const { lastEvent } = useLive();
  useEffect(() => { if (lastEvent?.category === "payment") reload(); }, [lastEvent, reload]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let n = 0;
    const timer = setInterval(async () => {
      n++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(timer); setPolling(false); toast.success("Payment confirmed! Thank you."); reload();
        } else if (n > 10) { clearInterval(timer); setPolling(false); }
      } catch { clearInterval(timer); setPolling(false); }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId, reload]);

  return (
    <div data-testid="payments-page">
      <Header title="Payments & Receipts" sub="Your payment history and downloadable receipts." />
      {polling && (
        <div className="mb-6 flex items-center gap-3 rounded-xl bg-amber-100 p-4 text-amber-800" data-testid="payment-pending">
          <Loader2 className="animate-spin" /> {t("Confirming your payment…")}
        </div>
      )}
      <div className="mb-4 flex justify-end">
        <Button variant="outline" size="sm" className="gap-1" data-testid="payments-statement-pdf"
          onClick={() => downloadPdf("/members/me/statement.pdf", "arrears-statement.pdf")}>
          <Download size={14} /> {t("Arrears statement (PDF)")}
        </Button>
      </div>
      <div className="space-y-2 lg:hidden" data-testid="payments-cards">
        {(history || []).map((p) => (
          <div key={p.id} className="glass rounded-xl p-3 text-sm" data-testid={`payment-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{toLocalISODate(p.created_at)} · <span className="rounded bg-parchment px-1.5 py-0.5">{p.method}</span></p>
                <p className="mt-1 break-words">{p.account_name && <span className="mr-1 rounded bg-gold-light px-1.5 py-0.5 text-[10px] font-semibold uppercase text-crimson">{p.account_name}</span>}{p.description}</p>
              </div>
              <span className="shrink-0 font-mono-accent font-semibold">${Number(p.amount).toFixed(2)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="font-mono-accent text-xs text-muted-foreground">{p.receipt_no}</span>
              <button onClick={() => downloadPdf(`/payments/${p.id}/receipt.pdf`, `receipt-${p.receipt_no}.pdf`)} data-testid={`receipt-pdf-m-${p.id}`}
                className="inline-flex h-9 items-center gap-1 rounded-md bg-crimson px-3 text-xs font-semibold text-parchment"><Download size={12} /> {t("PDF")}</button>
            </div>
          </div>
        ))}
        {(balance?.adjustments || []).map((a) => (
          <div key={a.id} className="rounded-xl border border-gold/30 bg-gold-light/40 p-3 text-sm" data-testid={`adjustment-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="text-xs text-muted-foreground">{toLocalISODate(a.created_at)} · {t("adjustment")}</p><p className="mt-1">{a.note}</p></div>
              <span className={`shrink-0 font-mono-accent font-semibold ${a.amount < 0 ? "text-palm" : "text-crimson"}`}>{a.amount < 0 ? "−" : "+"}${Math.abs(a.amount).toFixed(2)}</span>
            </div>
          </div>
        ))}
        {(!history || history.length === 0) && !(balance?.adjustments || []).length && <p className="glass rounded-xl p-6 text-center text-sm text-muted-foreground">{t("No payments yet.")}</p>}
      </div>
      <div className="glass hidden overflow-x-auto rounded-2xl lg:block">
        <table className="w-full min-w-[640px] text-sm" data-testid="payments-table">
          <thead className="bg-crimson text-parchment"><tr>
            <th className="p-3 text-left">{t("Date")}</th><th className="p-3 text-left">{t("Description")}</th>
            <th className="p-3 text-left">{t("Method")}</th><th className="p-3 text-left">{t("Receipt")}</th><th className="p-3 text-right">{t("Amount")}</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {(history || []).map((p) => (
              <tr key={p.id} className="border-b border-gold/15" data-testid={`payment-row-${p.id}`}>
                <td className="p-3">{toLocalISODate(p.created_at)}</td>
                <td className="p-3">{p.account_name && <span className="mr-1 rounded bg-gold-light px-1.5 py-0.5 text-[10px] font-semibold uppercase text-crimson" data-testid={`payment-account-${p.id}`}>{p.account_name}</span>}{p.description}</td>
                <td className="p-3"><span className="rounded bg-parchment px-2 py-0.5 text-xs">{p.method}</span></td>
                <td className="p-3 font-mono-accent text-xs">{p.receipt_no}</td>
                <td className="p-3 text-right font-mono-accent font-semibold">${Number(p.amount).toFixed(2)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => downloadPdf(`/payments/${p.id}/receipt.pdf`, `receipt-${p.receipt_no}.pdf`)} data-testid={`receipt-pdf-${p.id}`}
                    className="inline-flex items-center gap-1 rounded-md bg-crimson px-2 py-1 text-xs font-semibold text-parchment hover:bg-crimson-hover" title={t("Download receipt")}>
                    <Download size={12} /> {t("PDF")}
                  </button>
                </td>
              </tr>
            ))}
            {(balance?.adjustments || []).map((a) => (
              <tr key={a.id} className="border-b border-gold/15 bg-gold-light/40" data-testid={`adjustment-row-${a.id}`}>
                <td className="p-3">{toLocalISODate(a.created_at)}</td>
                <td className="p-3">{a.note}</td>
                <td className="p-3"><span className="rounded bg-parchment px-2 py-0.5 text-xs">{t("adjustment")}</span></td>
                <td className="p-3" />
                <td className={`p-3 text-right font-mono-accent font-semibold ${a.amount < 0 ? "text-palm" : "text-crimson"}`}>{a.amount < 0 ? "−" : "+"}${Math.abs(a.amount).toFixed(2)}</td>
                <td />
              </tr>
            ))}
            {(!history || history.length === 0) && !(balance?.adjustments || []).length && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">{t("No payments yet.")}</td></tr>}
          </tbody>
        </table>
      </div>
      {claims?.length > 0 && (
        <div className="mt-6">
          <CollapsibleList id="offline-claims" title={t("Offline claims")} rows={claims} empty={t("No offline claims yet.")}
            render={(c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg bg-parchment-card p-3 border border-gold/20" data-testid={`offline-claim-${c.id}`}>
                <span>${c.amount.toFixed(2)} via {c.method} <span className="ml-2 text-xs text-muted-foreground">{toLocalISODate(c.created_at)}</span></span>
                <StatusPill status={claimStanding(c.status)} />
              </div>
            )} />
        </div>
      )}
    </div>
  );
}

export function MemberEvents() {
  const { t } = useLang();
  const { data: events } = useApi("/events");
  const rsvp = async (id, status) => {
    try { await api.post(`/events/${id}/rsvp`, { status }); toast.success(`RSVP: ${status}`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="member-events-page">
      <Header title="Events" sub="RSVP, view agendas, and join virtual meetings." />
      <EventsBrowser events={events || []} render={(e) => (
          <div key={e.id} className="glass rounded-2xl p-6" data-testid={`m-event-${e.id}`}>
            <EventFlyer event={e} compact className="mb-4" testId={`m-event-flyer-${e.id}`} />
            <span className="eyebrow text-gold">{String(e.type || "").replace(/_/g, " ")}</span>
            {e.cancelled && <p className="mt-1 rounded-lg bg-crimson/10 px-3 py-2 text-sm font-semibold text-crimson" data-testid={`m-event-cancelled-${e.id}`}>{t("Cancelled")} — {e.cancel_reason}</p>}
            <h3 className="mt-1 font-serif-display text-2xl font-bold text-crimson">{e.title}</h3>
            <p className="mt-2 flex items-center gap-2 text-sm text-palm"><Calendar size={15} /> {e.date}{timeRange(e)}</p>
            <p className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={15} /> {e.location}</p>
            <RsvpBadge event={e} />
            {safeHref(e.virtual_link) && <a href={safeHref(e.virtual_link)} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 text-sm text-palm"><Video size={15} /> {t("Join virtual")}</a>}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => rsvp(e.id, "going")} className="bg-palm hover:bg-palm/90" data-testid={`rsvp-going-${e.id}`}>{t("Going")}</Button>
              <Button size="sm" variant="outline" onClick={() => rsvp(e.id, "maybe")} data-testid={`rsvp-maybe-${e.id}`}>{t("Maybe")}</Button>
              <Button size="sm" variant="ghost" onClick={() => rsvp(e.id, "no")} data-testid={`rsvp-no-${e.id}`}>{t("Can't go")}</Button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${t("Join us")}: ${e.title} — ${e.date}${e.location ? ` · ${e.location}` : ""}. ${t("RSVP here")}: ${window.location.origin}/events/${e.id}`)}`}
                target="_blank" rel="noreferrer" data-testid={`share-wa-group-${e.id}`}
                className="inline-flex items-center gap-1 rounded-md bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#1ebe5a]">
                <Share2 size={13} /> {t("Share to WhatsApp group")}
              </a>
            </div>
            <div className="mt-3"><AddToCalendar event={e} compact /></div>
          </div>
        )} />
      <div className="mt-10"><TeamupCalendar scope="public" title="Association calendar" /></div>
      <PastEvents />
      <MinutesList />
    </div>
  );
}

export function MemberWelfare() {
  const { t } = useLang();
  const { data: benefits } = useApi("/welfare/benefits");
  const { data: claims, reload } = useApi("/welfare/claims");
  const { data: standing } = useApi("/members/standing");
  const { lastEvent } = useLive();
  useEffect(() => { if (lastEvent?.category === "welfare") reload(); }, [lastEvent, reload]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ benefit_id: "", reason: "", documents: [] });

  const submit = async () => {
    try {
      await api.post("/welfare/claims", { benefit_id: form.benefit_id, reason: form.reason, documents: form.documents.map((d) => d.url) });
      toast.success("Welfare claim submitted."); setOpen(false); setForm({ benefit_id: "", reason: "", documents: [] }); reload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="member-welfare-page">
      <Header title="Welfare" sub="Submit claims and track their status." />
      {standing && !standing.welfare_eligible && (
        <div className="mb-6 rounded-xl bg-amber-100 p-4 text-sm text-amber-800" data-testid="welfare-ineligible">
          {t("Welfare benefits require good standing, plus 1 year of membership unless you are a grandfathered member.")}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Benefit catalog")}</h3>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-palm hover:bg-palm/90" disabled={!standing?.welfare_eligible} data-testid="welfare-new-claim">{t("New claim")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("Submit welfare claim")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t("Benefit")}</Label>
                    <Select value={form.benefit_id} onValueChange={(v) => setForm({ ...form, benefit_id: v })}>
                      <SelectTrigger data-testid="claim-benefit"><SelectValue placeholder={t("Select")} /></SelectTrigger>
                      <SelectContent>{(benefits || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name} (${b.amount})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("Reason")}</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="claim-reason" /></div>
                  <div><Label>{t("Supporting documents")}</Label>
                    <FileUpload purpose="welfare_document" testId="claim-doc-upload" onUploaded={(f) => setForm((s) => ({ ...s, documents: [...s.documents, f] }))} />
                    {form.documents.length > 0 && (
                      <ul className="mt-2 space-y-1 text-xs text-palm" data-testid="claim-doc-list">
                        {form.documents.map((d) => <li key={d.id}>• {d.filename}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
                <DialogFooter><Button onClick={submit} disabled={!form.benefit_id} className="bg-crimson" data-testid="claim-submit-btn">{t("Submit")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="mt-4 space-y-3">
            {(benefits || []).map((b) => (
              <div key={b.id} className="rounded-lg bg-parchment-card p-4 border border-gold/20">
                <div className="flex justify-between"><span className="font-semibold">{b.name}</span><span className="font-mono-accent text-palm">${b.amount}</span></div>
                <p className="text-sm text-muted-foreground">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold text-crimson">{t("My claims")}</h3>
          <div className="mt-4 space-y-3">
            {(claims || []).map((c) => (
              <div key={c.id} className="rounded-lg bg-parchment-card p-4 border border-gold/20" data-testid={`welfare-claim-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{c.benefit_name}</span>
                  <span className="rounded-full bg-crimson/10 px-2 py-0.5 text-xs font-mono-accent text-crimson">{c.status.replace(/_/g, " ")}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{c.reason}</p>
                {(c.documents || []).length > 0 && <p className="mt-1 text-xs text-palm">{c.documents.length} document(s) attached</p>}
                <div className="mt-2 space-y-1">
                  {(c.timeline || []).map((tl) => <p key={`${tl.status}-${tl.at}`} className="text-xs text-palm">• {tl.status.replace(/_/g," ")} — {tl.at?.slice(0,10)}</p>)}
                </div>
              </div>
            ))}
            {(!claims || claims.length === 0) && <p className="text-muted-foreground">{t("No claims yet.")}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Directory() {
  const [openMember, setOpenMember] = useState(null);
  const [q, setQ] = useState("");
  const { data, reload } = useApi(`/directory`, []);
  const [results, setResults] = useState(null);
  useEffect(() => { setResults(data); }, [data]);
  const search = async () => { const { data } = await api.get(`/directory?q=${encodeURIComponent(q)}`); setResults(data); };
  return (
    <div data-testid="directory-page">
      <Header title="Member Directory" sub="Connect with fellow members (opt-in only)." />
      <div className="mb-6 flex gap-2">
        <Input placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="directory-search" />
        <Button onClick={search} className="bg-crimson" data-testid="directory-search-btn">Search</Button>
      </div>
      <MemberCardDialog member={openMember} onClose={() => setOpenMember(null)} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(results || []).map((m) => (
          <div key={m.id} role="button" tabIndex={0} onClick={() => setOpenMember(m)} onKeyDown={(e) => e.key === "Enter" && setOpenMember(m)} className="glass cursor-pointer rounded-2xl p-5 transition-transform hover:-translate-y-0.5" data-testid={`directory-member-${m.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-crimson font-bold text-parchment" data-testid={`directory-avatar-${m.id}`}>{m.photo_url ? <AuthImage src={m.photo_url} alt="" className="h-full w-full object-cover" /> : m.name?.[0]}</div>
              <div><p className="font-semibold">{m.name}</p><p className="eyebrow text-gold">{m.lineage_status}</p></div>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              {m.phone && <a href={`tel:${m.phone}`} onClick={(e) => e.stopPropagation()} className="flex min-h-[40px] items-center gap-2 text-palm"><Phone size={14} /> {m.phone}</a>}
              {m.email && <a href={`mailto:${m.email}`} onClick={(e) => e.stopPropagation()} className="flex min-h-[40px] items-center gap-2 break-all text-palm"><Mail size={14} className="shrink-0" /> {m.email}</a>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Documents() {
  const { t } = useLang();
  const { isOfficer } = useAuth();
  const { data } = useApi("/documents");
  const { data: mine } = useApi("/members/me/files");
  const empty = !data || data.length === 0;
  const open = (d) => {
    if (d.url.startsWith("/api/")) downloadPdf(d.url.replace(/^\/api/, ""), `${d.title}.pdf`);
    else window.open(d.url, "_blank", "noreferrer");
  };
  return (
    <div data-testid="documents-page">
      <Header title="Documents" sub={isOfficer ? "All association documents (officer view — members only see what has been shared with them)." : "Documents shared with members by the executive."} />
      <div className="space-y-3">
        {(data || []).map((d) => (
          <div key={d.id} className="flex flex-col gap-3 rounded-xl border border-gold/30 bg-parchment-card p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`portal-doc-${d.id}`}>
            <div className="min-w-0"><span className="eyebrow text-gold">{d.category}{isOfficer && d.visibility === "officers" ? " · officers only" : ""}</span><h3 className="font-serif-display text-lg font-bold text-crimson">{d.title}</h3><p className="text-sm text-muted-foreground">{d.description}{d.shared_by ? ` · ${t("Shared by")} ${d.shared_by}` : ""}</p></div>
            <button onClick={() => open(d)} className="flex min-h-[44px] shrink-0 items-center justify-center gap-1 rounded-lg bg-crimson px-4 py-2 text-sm font-semibold text-parchment" data-testid={`portal-doc-open-${d.id}`}><Download size={15} /> {t("PDF")}</button>
          </div>
        ))}
        {empty && <p className="text-sm text-muted-foreground" data-testid="docs-empty">{t("No documents have been shared with members yet.")}</p>}
      </div>
      <div className="mt-10" data-testid="my-files">
        <CollapsibleList id="my-docs" title={t("My documents")} rows={mine} empty={t("No personal documents yet.")} searchable matcher={(f, q) => `${f.title || ""} ${f.original_filename || ""} ${f.purpose}`.toLowerCase().includes(q)} placeholder="Search my documents…"
          right={<Link to="/registration" className="text-sm text-palm underline" data-testid="portal-registration-link">{t("Complete the membership registration form")}</Link>}
          render={(f) => (
            <div key={f.id} className="flex items-center justify-between rounded-lg border border-gold/20 bg-parchment-card p-3 text-sm" data-testid={`my-file-${f.id}`}>
              <span>{f.title || f.original_filename} <span className="eyebrow ml-2 text-gold">{f.purpose.replace(/_/g, " ")}</span></span>
              <button onClick={() => downloadPdf(`/files/${f.id}`, f.original_filename)} className="text-crimson underline" data-testid={`my-file-dl-${f.id}`}>{t("Download")}</button>
            </div>
          )} />
      </div>
    </div>
  );
}

export function Notifications() {
  const { t } = useLang();
  const { user } = useAuth();
  const { lastEvent, bump } = useLive();
  const { data, reload } = useApi("/notifications");
  useEffect(() => { if (lastEvent) reload(); }, [lastEvent, reload]);
  const { data: prefs, reload: reloadPrefs } = useApi("/notifications/preferences");
  const markAll = async () => { await api.post("/notifications/read-all"); bump(() => 0); reload(); };
  const togglePref = async (k, v) => { const updated = { ...prefs, [k]: v }; await api.put("/notifications/preferences", updated); reloadPrefs(); };
  return (
    <div data-testid="notifications-page">
      <Header title="Notifications" sub="Notices and your preferences." />
      <Tabs defaultValue="inbox">
        <TabsList><TabsTrigger value="inbox" data-testid="tab-inbox">{t("Inbox")}</TabsTrigger><TabsTrigger value="prefs" data-testid="tab-prefs">{t("Preferences")}</TabsTrigger></TabsList>
        <TabsContent value="inbox">
          <div className="mb-3 flex justify-end"><Button variant="outline" onClick={markAll} data-testid="mark-all-read"><CheckCheck size={15} className="mr-1" /> {t("Mark all read")}</Button></div>
          <NotificationGroups notifications={data?.notifications || []} onChanged={() => { reload(); bump((n) => Math.max(0, n - 1)); }} />
        </TabsContent>
        <TabsContent value="prefs">
          <div className="glass max-w-md rounded-2xl p-6 space-y-4">
            {prefs && ["dues", "events", "welfare", "levies", "elections"].map((k) => (
              <div key={k} className="flex items-center justify-between">
                <Label className="capitalize">{k} notifications</Label>
                <Switch checked={prefs[k]} onCheckedChange={(v) => togglePref(k, v)} data-testid={`pref-${k}`} />
              </div>
            ))}
            {prefs && (
              <div className="flex items-center justify-between border-t border-gold/20 pt-4">
                <div><Label>{t("Text messages (SMS)")}</Label><p className="text-xs text-muted-foreground">Dues & meeting reminders to {user?.phone || "your phone (add one in Profile)"}</p></div>
                <Switch checked={!!prefs.sms} onCheckedChange={(v) => togglePref("sms", v)} data-testid="pref-sms" />
              </div>
            )}
            {prefs && (
              <div className="border-t border-gold/20 pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div><Label>{t("Phone push alerts")}</Label><p className="text-xs text-muted-foreground">Notices pop up on this device even when the app is closed</p></div>
                  <Switch checked={prefs.push !== false} onCheckedChange={(v) => togglePref("push", v)} data-testid="pref-push" />
                </div>
                <PushToggle compact />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const fullAddress = (u) => [u.address, u.address2, [u.city, u.state].filter(Boolean).join(", "), u.postal_code].map((x) => (x || "").trim()).filter(Boolean).join(", ");

export function Profile() {
  const { user, refresh } = useAuth();
  const tips = useTips();
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [dep, setDep] = useState({ name: "", relationship: "", dob: "" });
  useEffect(() => { if (user) setForm({ name: user.name, phone: user.phone || "", address: user.address || "", address2: user.address2 || "", city: user.city || "", state: user.state || "", postal_code: user.postal_code || "", language_pref: user.language_pref || "en", directory_visible: user.directory_visible, spotlight_visible: user.spotlight_visible !== false, photo_url: user.photo_url || "", dob: user.dob || "", birthday_private: !!user.birthday_private }); }, [user]);
  if (!form) return null;
  const save = async () => { await api.put("/members/me", form); await refresh(); toast.success("Profile updated"); };
  const addDep = async () => { await api.post("/members/me/dependents", dep); await refresh(); setDep({ name: "", relationship: "", dob: "" }); toast.success("Dependent added"); };
  const rmDep = async (id) => { await api.delete(`/members/me/dependents/${id}`); await refresh(); };
  return (
    <div data-testid="profile-page">
      <Header title="My Profile" sub="Manage your details, dependents, and directory visibility." />
      <div className="mb-6 glass flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4" data-testid="profile-tips-prefs">
        <div className="flex items-center gap-3 text-sm"><Lightbulb size={18} className="text-gold" /><Label htmlFor="profile-tips-switch">Show in-app tips as I navigate</Label>{tips?.state && <Switch id="profile-tips-switch" checked={tips.state.enabled} onCheckedChange={tips.setEnabled} data-testid="profile-tips-toggle" />}</div>
        <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => tips?.reset().then(() => toast.success("Tips will show again"))} data-testid="profile-tips-reset">Reset tips</Button><Link to="/portal/help" className="self-center text-sm underline text-palm" data-testid="profile-help-link">Open the tip sheet</Link></div>
      </div>
      <div className="mb-6 glass flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4" data-testid="my-data-export">
        <div className="text-sm"><b className="font-serif-display text-crimson">Your data, your copy.</b> <span className="text-muted-foreground">Download everything we hold about you — profile, payments, welfare claims, attendance and preferences — as a PDF. See our <Link to="/privacy" className="underline text-palm">Privacy Policy</Link>.</span></div>
        <Button variant="outline" className="gap-2 border-gold text-crimson" onClick={() => downloadPdf("/members/me/data-export.pdf", "my-data.pdf")} data-testid="my-data-export-btn"><Download size={15} /> Download my data (PDF)</Button>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-6 space-y-3">
          <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Details")}</h3>
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => user.photo_url && setPhotoOpen(true)} className={`flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-crimson text-2xl font-bold text-parchment ${user.photo_url ? "cursor-zoom-in ring-2 ring-gold/60" : ""}`} aria-label="View profile photo" data-testid="profile-avatar">
              {user.photo_url ? <AuthImage src={user.photo_url} alt={user.name} className="h-full w-full object-cover" /> : user.name?.[0]}
            </button>
            {photoOpen && user.photo_url && createPortal(
              <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/95 p-4 animate-in fade-in" role="dialog" aria-modal="true" onClick={() => setPhotoOpen(false)} data-testid="profile-photo-lightbox">
                <button type="button" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-parchment" aria-label="Close" data-testid="profile-photo-close"><X size={22} /></button>
                <AuthImage src={user.photo_url} alt={user.name} className="max-h-[80vh] max-w-full rounded-2xl object-contain shadow-2xl" />
                <p className="mt-4 font-serif-display text-xl text-parchment">{user.name}</p>
              </div>, document.body)}
            <div className="flex-1"><Label>{t("Profile photo")}</Label>
              <FileUpload purpose="profile_photo" accept="image/*" label={t("Upload photo")} testId="profile-photo-upload" onUploaded={async () => { await refresh(); toast.success("Photo updated"); }} /></div>
          </div>
          <div><Label>{t("Full name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
          <div><Label>{t("Phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
          <div><Label>{t("Date of birth")}</Label><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="profile-dob" /><p className="text-xs text-muted-foreground">Used for birthday greetings; only the month and day are shared with members.</p></div>
          <div><Label>{t("Street address")}</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" /></div>
          <div><Label>Apt / Unit (optional)</Label><Input value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} data-testid="profile-address2" /></div>
          <div className="grid grid-cols-[1fr_80px_110px] gap-2">
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city" /></div>
            <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} maxLength={2} placeholder="MA" data-testid="profile-state" /></div>
            <div><Label>ZIP</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="profile-postal" /></div>
          </div>
          {fullAddress(form) && <p className="rounded-lg bg-parchment-card px-3 py-2 text-sm text-foreground/80" data-testid="profile-full-address"><MapPin size={13} className="mr-1 inline text-gold" />{fullAddress(form)}</p>}
          <div>
            <Label>{t("Language preference")}</Label>
            <Select value={form.language_pref} onValueChange={(v) => setForm({ ...form, language_pref: v })}>
              <SelectTrigger data-testid="profile-lang"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="tw">Akuapem Twi</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between"><Label>{t("Visible in directory")}</Label>
            <Switch checked={form.directory_visible} onCheckedChange={(v) => setForm({ ...form, directory_visible: v })} data-testid="profile-visible" /></div>
          <div className="flex items-center justify-between"><Label>{t("Show me in the public Akwaaba new-member strip")}</Label>
            <Switch checked={form.spotlight_visible} onCheckedChange={(v) => setForm({ ...form, spotlight_visible: v })} data-testid="profile-spotlight-visible" /></div>
          <div className="flex items-center justify-between gap-3"><div><Label>{t("Keep my birthday private")}</Label><p className="text-xs text-muted-foreground">No birthday greetings, announcements or WhatsApp posts about you.</p></div>
            <Switch checked={!!form.birthday_private} onCheckedChange={(v) => setForm({ ...form, birthday_private: v })} data-testid="profile-birthday-private" /></div>
          <Button onClick={save} className="w-full bg-crimson hover:bg-crimson-hover" data-testid="profile-save">{t("Save changes")}</Button>
        </div>
        <MyConsentCard user={user} onSaved={refresh} />
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif-display text-xl font-bold text-crimson">{t("Dependents")}</h3>
          <div className="mt-3 space-y-2">
            {(user.dependents || []).map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg bg-parchment-card p-3 border border-gold/20" data-testid={`dep-${d.id}`}>
                <span>{d.name} — <span className="text-muted-foreground">{d.relationship}</span></span>
                <Button size="sm" variant="ghost" onClick={() => rmDep(d.id)} className="text-destructive">{t("Remove")}</Button>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 border-t border-gold/20 pt-4">
            <Input placeholder={t("Name")} value={dep.name} onChange={(e) => setDep({ ...dep, name: e.target.value })} data-testid="dep-name" />
            <Input placeholder={t("Relationship")} value={dep.relationship} onChange={(e) => setDep({ ...dep, relationship: e.target.value })} data-testid="dep-rel" />
            <Button onClick={addDep} disabled={!dep.name} className="w-full bg-palm hover:bg-palm/90" data-testid="dep-add">{t("Add dependent")}</Button>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-6"><SecurityNotices /><SecurityPanel /></div>
      </div>
    </div>
  );
}

export function Elections() {
  const { t } = useLang();
  const { isOfficer } = useAuth();
  const { data, reload } = useApi("/elections");
  const { data: directory } = useApi("/members/roster");
  const { data: standing } = useApi("/members/standing");
  const [nom, setNom] = useState({ election_id: "", nominee_id: "", statement: "" });
  const vote = async (electionId, nominationId) => {
    try { await api.post("/elections/vote", { election_id: electionId, nomination_id: nominationId }); toast.success("Ballot cast!"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const nominate = async () => {
    try { await api.post("/elections/nominate", nom); toast.success("Nomination submitted for Electoral Commissioner approval"); setNom({ election_id: "", nominee_id: "", statement: "" }); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const openForNoms = (data || []).filter((el) => !el.is_closed_for_nominations && new Date(el.closes_at) > new Date());
  return (
    <div data-testid="elections-page">
      <Header title="Elections" sub="Nominate members for open positions and cast your ballot." />
      {openForNoms.length > 0 && (
        <div className="glass mb-6 rounded-2xl p-6" data-testid="nomination-form">
          <h3 className="font-serif-display text-xl font-bold text-palm">{t("Nominate a member")}</h3>
          <p className="text-sm text-muted-foreground">Any member may nominate. The Electoral Commissioner approves nominees before voting opens.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Select value={nom.election_id} onValueChange={(v) => setNom({ ...nom, election_id: v })}>
              <SelectTrigger data-testid="nom-position"><SelectValue placeholder="Open position" /></SelectTrigger>
              <SelectContent>{openForNoms.map((el) => <SelectItem key={el.id} value={el.id}>{el.position} — {el.title}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={nom.nominee_id} onValueChange={(v) => setNom({ ...nom, nominee_id: v })}>
              <SelectTrigger data-testid="nom-member"><SelectValue placeholder="Member to nominate" /></SelectTrigger>
              <SelectContent>{(directory || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.member_id ? ` · ${m.member_id}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Why this member? (optional)" value={nom.statement} onChange={(e) => setNom({ ...nom, statement: e.target.value })} data-testid="nom-statement" />
          </div>
          <Button onClick={nominate} disabled={!nom.election_id || !nom.nominee_id} className="mt-3 bg-palm hover:bg-palm/90" data-testid="nom-submit">Submit nomination</Button>
        </div>
      )}
      <div className="space-y-6">
        {(data || []).length === 0 && <p className="text-muted-foreground">No elections at this time.</p>}
        {(data || []).map((el) => (
          <div key={el.id} className="glass rounded-2xl p-6" data-testid={`election-${el.id}`}>
            <div className="flex items-center justify-between">
              <div><span className="eyebrow text-gold">{el.position}</span><h3 className="font-serif-display text-2xl font-bold text-crimson">{el.title}</h3></div>
              <span className={`rounded-full px-3 py-1 text-xs font-mono-accent ${el.is_open ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>{el.is_open ? "OPEN" : "CLOSED"}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{el.description}</p>
            {el.is_open && standing && !standing.good_standing && (
              <p className="mt-2 rounded-lg bg-amber-100 p-2 text-xs text-amber-800" data-testid={`vote-ineligible-${el.id}`}>Voting is open, but only members in good standing may vote. Settle your dues and keep attendance at 50%+ to unlock your ballot.</p>
            )}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {(el.candidates || []).map((c) => (
                <div key={c.id} className="rounded-lg bg-parchment-card p-4 border border-gold/20">
                  <p className="font-semibold">{c.candidate_name}</p>
                  <p className="text-sm text-muted-foreground">{c.statement}</p>
                  {el.is_open && (standing?.good_standing
                    ? <Button size="sm" onClick={() => vote(el.id, c.id)} className="mt-2 bg-crimson" data-testid={`vote-${c.id}`}><VoteIcon size={14} className="mr-1" /> Vote</Button>
                    : <Button size="sm" disabled className="mt-2" title="Good standing required" data-testid={`vote-locked-${c.id}`}><VoteIcon size={14} className="mr-1" /> Vote</Button>)}
                </div>
              ))}
              {(el.candidates || []).length === 0 && <p className="text-sm text-muted-foreground">No approved candidates yet.</p>}
            </div>
            <ElectionResults election={el} isOfficer={isOfficer} onChanged={reload} />
          </div>
        ))}
      </div>
    </div>
  );
}
